import { useState, useRef, useEffect, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import { Eraser, FileDown, Loader2, PackageCheck, CheckCircle, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTransferById, useUpdateTransferStatus, StockTransfer } from '@/hooks/useStockTransfers';
import { generateStockTransferPDF, generateTransferQRCode } from '@/utils/pdfGenerator';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface TransferReceiveModalProps {
  transfer: StockTransfer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TransferReceiveModal({ transfer, open, onOpenChange }: TransferReceiveModalProps) {
  const { user } = useAuth();
  const { data: detail } = useTransferById(transfer.id);
  const updateStatus = useUpdateTransferStatus();

  const [signerName, setSignerName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // qty_received e divergence_notes por item id
  const [itemReceive, setItemReceive] = useState<
    Record<string, { qty_received: number; divergence_notes: string }>
  >({});

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signaturePadRef.current) return;
    const saved = signaturePadRef.current.isEmpty() ? null : signaturePadRef.current.toData();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);
    signaturePadRef.current.clear();
    if (saved) signaturePadRef.current.fromData(saved);
  }, []);

  const setCanvasCallback = useCallback((canvas: HTMLCanvasElement | null) => {
    canvasRef.current = canvas;
    if (canvas && open) setCanvasReady(true);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCanvasReady(false);
      setConfirmed(false);
      setSignerName('');
      setItemReceive({});
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [open, resizeCanvas]);

  useEffect(() => {
    if (canvasReady && canvasRef.current && open) {
      const timer = setTimeout(() => {
        try {
          if (signaturePadRef.current) signaturePadRef.current.off();
          signaturePadRef.current = new SignaturePad(canvasRef.current!, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5,
          });
          resizeCanvas();
        } catch (e) {
          console.error('SignaturePad init error:', e);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [canvasReady, open, resizeCanvas]);

  // Inicializa com qty_sent como valor padrão de qty_received
  useEffect(() => {
    if (detail?.items) {
      const initial: Record<string, { qty_received: number; divergence_notes: string }> = {};
      detail.items.forEach((item) => {
        initial[item.id] = {
          qty_received: item.qty_received ?? (item.qty_sent ?? item.qty_requested),
          divergence_notes: item.divergence_notes ?? '',
        };
      });
      setItemReceive(initial);
    }
  }, [detail?.items]);

  const handleClear = () => signaturePadRef.current?.clear();

  // Verifica se há divergências
  const hasDivergence = detail?.items?.some((item) => {
    const recv = itemReceive[item.id]?.qty_received;
    return recv !== undefined && item.qty_sent !== null && recv !== item.qty_sent;
  });

  const handleReceive = async () => {
    if (!signerName.trim()) { toast.error('Informe o nome do receptor'); return; }
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast.error('Assinatura obrigatória');
      return;
    }
    if (!user?.id) return;

    setIsSaving(true);
    try {
      const signatureData = signaturePadRef.current.toDataURL();
      const now = new Date().toISOString();

      const itemUpdates = detail?.items?.map((item) => {
        const recv = itemReceive[item.id];
        return {
          id: item.id,
          qty_received: recv?.qty_received ?? (item.qty_sent ?? item.qty_requested),
          divergence_notes: recv?.divergence_notes || undefined,
        };
      }) ?? [];

      // Atualiza status para 'recebido' — o trigger disparará stock_movements e evento financeiro
      await updateStatus.mutateAsync({
        id: transfer.id,
        status: 'recebido',
        extra: {
          received_by: user.id,
          received_at: now,
          receive_signature_data: signatureData,
          receive_signature_name: signerName,
          received_by_name: signerName,
        },
        items: itemUpdates,
      });

      setConfirmed(true);
    } catch {
      // erro já tratado no hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!detail) return;
    setIsGeneratingPDF(true);
    try {
      const { data: companyData } = await supabase
        .from('companies')
        .select('name, cnpj, logo_url')
        .eq('id', transfer.company_id_from)
        .single();

      const qrCodeDataUrl = await generateTransferQRCode(transfer.id);

      const pdfBlob = await generateStockTransferPDF({
        document_number: transfer.document_number,
        created_at: transfer.created_at,
        dispatched_at: detail.dispatched_at,
        company: {
          name: companyData?.name ?? transfer.company_from?.name ?? '',
          cnpj: companyData?.cnpj ?? undefined,
          logo_url: companyData?.logo_url ?? undefined,
        },
        company_from: transfer.company_from ?? { name: '' },
        company_to: transfer.company_to ?? { name: '' },
        deposit_from: transfer.deposit_from ?? { name: '' },
        deposit_to: transfer.deposit_to ?? { name: '' },
        items: (detail.items ?? []).map((item) => ({
          product_name: item.product?.name ?? '',
          sku: item.product?.sku ?? null,
          qty_requested: item.qty_requested,
          qty_sent: item.qty_sent,
          unit_cost: item.unit_cost,
          total: item.qty_sent != null && item.unit_cost != null
            ? item.qty_sent * item.unit_cost
            : null,
        })),
        notes: detail.notes,
        dispatched_by_name: detail.dispatched_by_name,
        dispatch_signature_data: detail.dispatch_signature_data,
        qr_code_data_url: qrCodeDataUrl,
      });

      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${transfer.document_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF gerado com sucesso!');
    } catch (e) {
      console.error('Erro ao gerar PDF:', e);
      toast.error('Erro ao gerar PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!isSaving) onOpenChange(v); }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="h-5 w-5 text-green-600" />
              {confirmed ? 'Recebimento Confirmado!' : `Receber — ${transfer.document_number}`}
            </DialogTitle>
            {!isSaving && (
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <DialogDescription>
            {confirmed
              ? 'Estoque atualizado em ambas as unidades.'
              : 'Registre as quantidades recebidas. Divergências são permitidas e nunca bloqueiam o recebimento.'}
          </DialogDescription>
        </DialogHeader>

        {confirmed ? (
          <div className="flex-1 flex flex-col items-center justify-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-lg font-semibold">{transfer.document_number}</p>
            {detail?.has_divergence && (
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 gap-1">
                <AlertTriangle className="h-3 w-3" />
                Divergência registrada
              </Badge>
            )}
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              Estoque atualizado. Evento financeiro criado para processamento no Sprint 5.
            </p>
            <Button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              variant="outline"
              className="gap-2 mt-2"
            >
              {isGeneratingPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              Baixar Documento PDF
            </Button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 px-1 min-h-0">
            {/* Aviso sobre divergências */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>Divergências são registradas automaticamente mas nunca bloqueiam o recebimento.</p>
            </div>

            {/* Itens com qty_received */}
            <div>
              <Label className="mb-3 block text-sm font-semibold">Quantidades Recebidas</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">Produto</th>
                      <th className="px-3 py-2 text-right w-28">Separado</th>
                      <th className="px-3 py-2 text-right w-32">Recebido</th>
                      <th className="px-3 py-2 text-left w-40">Nota Divergência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail?.items ?? []).map((item, idx) => {
                      const recv = itemReceive[item.id];
                      const isDivergent = recv?.qty_received !== undefined &&
                        item.qty_sent !== null &&
                        recv.qty_received !== item.qty_sent;

                      return (
                        <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/30'}>
                          <td className="px-3 py-2">
                            <p className="font-medium">{item.product?.name}</p>
                            {item.product?.sku && (
                              <p className="text-xs text-muted-foreground font-mono">{item.product.sku}</p>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                            {item.qty_sent ?? '—'}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              min={0}
                              step={0.0001}
                              value={recv?.qty_received ?? (item.qty_sent ?? item.qty_requested)}
                              onChange={(e) =>
                                setItemReceive((prev) => ({
                                  ...prev,
                                  [item.id]: {
                                    ...prev[item.id],
                                    qty_received: parseFloat(e.target.value) || 0,
                                  },
                                }))
                              }
                              className={`h-8 text-right font-mono text-sm ${isDivergent ? 'border-orange-400 bg-orange-50' : ''}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            {isDivergent && (
                              <Textarea
                                placeholder="Motivo..."
                                rows={1}
                                value={recv?.divergence_notes ?? ''}
                                onChange={(e) =>
                                  setItemReceive((prev) => ({
                                    ...prev,
                                    [item.id]: {
                                      ...prev[item.id],
                                      divergence_notes: e.target.value,
                                    },
                                  }))
                                }
                                className="text-xs min-h-0 h-8 py-1"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {hasDivergence && (
                <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Divergência detectada — será registrada ao confirmar
                </p>
              )}
            </div>

            {/* Nome do receptor */}
            <div className="space-y-2">
              <Label>Nome do Receptor *</Label>
              <Input
                placeholder="Nome completo do receptor"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                disabled={isSaving}
                className="h-11"
              />
            </div>

            {/* Assinatura digital */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Assinatura Digital *</Label>
                <Button type="button" variant="ghost" size="sm" onClick={handleClear} disabled={isSaving}>
                  <Eraser className="h-4 w-4 mr-1" />
                  Limpar
                </Button>
              </div>
              <div className="border-2 border-dashed border-border rounded-lg p-4">
                <canvas
                  ref={setCanvasCallback}
                  className="w-full h-32 border border-border rounded bg-white cursor-crosshair touch-none"
                  style={{ touchAction: 'none' }}
                />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Ao assinar: trigger processa stock_movements na Matriz e na Filial + evento financeiro.
                </p>
              </div>
            </div>

            {/* Download PDF */}
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="w-full gap-2"
            >
              {isGeneratingPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              Baixar Documento PDF
            </Button>
          </div>
        )}

        {!confirmed && (
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button
              onClick={handleReceive}
              disabled={isSaving}
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}
              Confirmar Recebimento
            </Button>
          </DialogFooter>
        )}

        {confirmed && (
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
